import React, { useCallback, useState } from 'react';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import Modal from 'buyside/components/presentation/Modal/modal';

const RankingsMethodologyModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        onClick={() => setIsOpen(true)}
        size={BUTTON_SIZES.SMALL}
        text="Click to View the Sumzero Ranking Methodology"
        type={BUTTON_TYPES.LINK}
      />
    ),
    []
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      renderOpenModalButton={renderOpenModalButton}
      size="xl"
      title="Ranking Methodology"
    >
      <p>
        Each month SumZero publishes rankings of our top users based on the performance of their
        ideas. Rankings are calculated to highlight users with the highest risk adjusted returns
        relative to the market. There are four key points to the ranking methodology we use:
      </p>
      <ul>
        <li>
          <strong>Risk Aversion:</strong> Our ranking algorithm attempts to model the preferences of
          risk averse investors. Therefore large gains are rewarded lightly and large losses are
          punished heavily. This favors users who are consistently profitable above those with a
          highly volatile approach.
        </li>
        <li>
          <strong>Benchmarking:</strong> For each idea that is submitted to SumZero, an appropriate
          benchmark is selected based on the underlying security’s region of domicile and market
          cap. The ideas’ returns relative to this benchmark is used in our rankings calculations to
          select users that outperform broader market trends.
        </li>
        <li>
          <strong>Quantity:</strong> To be considered for a ranking, users must submit a minimum
          number of research ideas relevant to that ranking category. Additionally, users who submit
          more quality ideas are ranked higher to reward repeatable idea generation.
        </li>
        <li>
          <strong>Recency:</strong> The effect an idea's return has on rankings decays slowly with
          time, so more recent ideas have a stronger effect than old ideas. This ensures that the
          most highly ranked analysts are those that stay up to date with their research.
        </li>
      </ul>
      <p>
        Why get ranked? Securing placement on the SumZero Rankings list is a function of being a
        consistent and effective analyst. Benefits include:
      </p>
      <ul>
        <li>
          <strong>Increased Exposure:</strong> Strong performance will drive increased exposure for
          you, your firm, and your ideas.
        </li>
        <li>
          <strong>Thought-Leadership:</strong> Consistent appearances on the Rankings list entitles
          relevant members to expertise in a given sector, region, or trade type.
        </li>
        <li>
          <strong>Professional Achievement:</strong> SumZero is a large community where exceptional
          performance is acknowledged/rewarded both by peers in the industry, as well as the
          hundreds of institutional investors using the SumZero platform for fund allocation
          purposes.
        </li>
      </ul>
      <p>To improve your ranking, we suggest a few things:</p>
      <ul>
        <li>
          <strong>Close ideas that didn’t work out.</strong> An idea can crash for any number of
          reasons, and our system ensures that no user’s ranking is lowered beyond recovery.
          However, it is still important to cut your losses since they are punished heavily in our
          ranking algorithm.
        </li>
        <li>
          <strong>Submit more ideas.</strong> Each ranking requires the user to submit a minimum
          number of relevant ideas. This cutoff is higher for more popular categories (e.g. North
          America, Large Cap) and and lower for less popular categories (e.g. Oceania, Accounting
          Fraud). Additionally, our ranking algorithm rewards users that submit more ideas.
        </li>
        <li>
          <strong>Submit your best ideas.</strong> Since rankings are calculated on a benchmark
          adjusted basis, generic long term holds on blue chip names are not going to help your
          ranking much, and may even hurt it if your picks underperform their benchmarks.
        </li>
      </ul>
      <p>
        Above all, top ranked users are those that continue to submit the fewest poor performing
        ideas and the most top performing ideas.
      </p>
      <p>
        Only the top ranked members on a respective Ranking are displayed publicly. Some members who
        qualify for a Ranking, but aren't towards the top of it, may be ranked privately. These
        members will see their own private ranks relative to their peers, but only appear publicly
        when their position on the list improves.
      </p>
      <p>
        The number of members ranked in a given category is variable based on the size of the
        dataset available. As a particular dataset grows larger, we can/will expand the size of the
        publicly displayed members in that respective list. If the dataset is small, our public
        ranking of that dataset will remain relatively small.
      </p>
    </Modal>
  );
};

export default RankingsMethodologyModal;
